import { onError } from '@apollo/client/link/error'
import { RetryLink } from '@apollo/client/link/retry'
import { offsetLimitPagination } from '@apollo/client/utilities'
import { InMemoryCache } from 'apollo-cache-inmemory'
import { ApolloClient } from 'apollo-client'
import { concat, split } from 'apollo-link'
import { setContext } from 'apollo-link-context'
import { WebSocketLink } from 'apollo-link-ws'
import { createUploadLink } from 'apollo-upload-client'
import { getMainDefinition } from 'apollo-utilities'
import { createNetworkStatusNotifier } from 'react-apollo-network-status'
import { SubscriptionClient } from 'subscriptions-transport-ws'

import { SessionHelper } from '../utils'

const options = {
	httpUri: process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/graphql',
	wsUri: process.env.REACT_APP_GRAPHQL_WS_API || 'ws://localhost:4000/graphql'
}

const { link: networkStatusLink, useApolloNetworkStatus } =
	createNetworkStatusNotifier()

const MAX_RETRIES = 8

const retryLink = new RetryLink({
	attempts: {
		max: MAX_RETRIES,
		retryIf: (error, _operation) => {
			if (!error) return false
			// HTTP error from Traefik (502, 503, 504 during scale-down)
			if (error.statusCode >= 500) return true
			// Pure network failure (connection refused, DNS, timeout) — no response
			return !error.result
		}
	},
	delay: {
		initial: 500,
		max: 15000,
		jitter: true
	}
})

const errorLink = onError(({ graphQLErrors, networkError }) => {
	if (graphQLErrors) {
		graphQLErrors.forEach(({ message, locations, path }) =>
			// eslint-disable-next-line no-console
			console.log(
				`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
			)
		)
	}
	// eslint-disable-next-line no-console
	if (networkError) console.log(`[Network error]: ${networkError}`)
})

const cache = new InMemoryCache({
	typePolicies: {
		Query: {
			fields: {
				CommunicationLogs: offsetLimitPagination()
			}
		}
	}
})

let httpLink = createUploadLink({
	uri: options.httpUri
})

if (options.wsUri) {
	const subscriptionClient = new SubscriptionClient(options.wsUri, {
		reconnect: true,
		reconnectionAttempts: Infinity,
		timeout: 30000,
		inactivityTimeout: 30000,
		lazy: true,
		connectionParams: () => ({
			// Read token dynamically so reconnects after scaling use the current token
			authToken: SessionHelper.token
		})
	})

	const wsLink = new WebSocketLink(subscriptionClient)

	httpLink = split(
		({ query }) => {
			const definition = getMainDefinition(query)
			return (
				definition.kind === 'OperationDefinition' &&
				definition.operation === 'subscription'
			)
		},
		wsLink,
		httpLink
	)
}

const authLink = setContext((_, { headers }) => {
	const token = SessionHelper.token
	return {
		headers: {
			...headers,
			Authorization: token ? `Bearer ${token}` : ''
		}
	}
})

export { useApolloNetworkStatus, cache }

export default new ApolloClient({
	link: concat(
		networkStatusLink,
		concat(errorLink, concat(retryLink, authLink.concat(httpLink)))
	),
	cache,
	connectToDevTools: true
})
